/**
 * GalleryTab - Image Gallery carousel management (CMS)
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Plus, Trash2, Upload, Image as ImageIcon, ChevronUp, ChevronDown, RotateCcw, Smartphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveImageUrl = (url) => {
  if (!url) return '';
  return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

export function GalleryTab({ content, setContent }) {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const gallery = content?.gallery || {};
  const images = gallery.images || [];

  const updateGallery = (patch) => {
    setContent((prev) => ({
      ...prev,
      gallery: { ...(prev.gallery || {}), ...patch },
    }));
  };

  const updateImage = (index, field, value) => {
    const next = [...images];
    next[index] = { ...next[index], [field]: value };
    updateGallery({ images: next });
  };

  const addImage = () => {
    const newIndex = images.length;
    updateGallery({
      images: [...images, { id: Date.now().toString(), url: '', title: '', subtitle: '' }],
    });
    toast.success(`Image ${newIndex + 1} added at the bottom`, { duration: 2500 });
    // Scroll the new row into view after it renders
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="gallery-image-row-${newIndex}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('gallery-row-pulse');
        setTimeout(() => el.classList.remove('gallery-row-pulse'), 1200);
      }
    }, 80);
  };

  const removeImage = (index) => {
    updateGallery({ images: images.filter((_, i) => i !== index) });
  };

  const moveImage = (from, to) => {
    if (to < 0 || to >= images.length) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    updateGallery({ images: next });
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    setUploadingIndex(index);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/landing/upload-gallery-image`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateImage(index, 'url', res.data.image_url);
    } catch (err) {
      console.error('Gallery image upload failed:', err);
    } finally {
      setUploadingIndex(null);
    }
  };

  return (
    <Card data-testid="gallery-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-emerald-600" /> Image Gallery
          </CardTitle>
          <CardDescription>
            Carousel of images with a centred slide and fading neighbours. Reorder, upload, and add titles.
          </CardDescription>
        </div>
        <Button onClick={addImage} variant="outline" data-testid="gallery-add-image">
          <Plus className="w-4 h-4 mr-2" /> Add Image
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Section Heading */}
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <span className="text-xs font-medium text-gray-500 block">Section Heading</span>
          <div>
            <Label>Badge Text</Label>
            <Input
              value={gallery.badge || ''}
              onChange={(e) => updateGallery({ badge: e.target.value })}
              placeholder="e.g., GALLERY"
              data-testid="gallery-heading-badge"
            />
          </div>
          <div>
            <Label>Title</Label>
            <Input
              value={gallery.title || ''}
              onChange={(e) => updateGallery({ title: e.target.value })}
              placeholder="e.g., A Look Inside"
              data-testid="gallery-heading-title"
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={gallery.subtitle || ''}
              onChange={(e) => updateGallery({ subtitle: e.target.value })}
              placeholder="e.g., Moments from the app, in action on the pitch."
              data-testid="gallery-heading-subtitle"
            />
          </div>
        </div>

        {/* Phone Frame option */}
        <div className="bg-emerald-50/50 border border-emerald-200 p-4 rounded-lg space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <Smartphone className="w-4 h-4 text-emerald-700 mt-0.5" />
              <div>
                <span className="text-sm font-semibold text-emerald-900 block">Display images inside a mobile phone frame</span>
                <p className="text-xs text-emerald-800/70 mt-0.5">
                  Wraps each gallery image in the same iPhone-style mockup used on the Voice Features section.
                  Best for app screenshots — uses portrait orientation (9:19.5 aspect feels most natural).
                </p>
              </div>
            </div>
            <Switch
              checked={!!gallery.use_phone_frame}
              onCheckedChange={(v) => updateGallery({ use_phone_frame: v })}
              data-testid="gallery-phone-frame-toggle"
            />
          </div>

          {gallery.use_phone_frame && (
            <div className="space-y-4 pt-2 border-t border-emerald-200/60">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm">Width (px)</Label>
                  <Input
                    type="number" min="240" max="640"
                    placeholder="360"
                    value={gallery.phone_width_px ?? ''}
                    onChange={(e) => updateGallery({ phone_width_px: e.target.value === '' ? undefined : Number(e.target.value) })}
                    data-testid="gallery-phone-width"
                  />
                </div>
                <div>
                  <Label className="text-sm">Min height — desktop (px)</Label>
                  <Input
                    type="number" min="360" max="900"
                    placeholder="560"
                    value={gallery.phone_min_height_px ?? ''}
                    onChange={(e) => updateGallery({ phone_min_height_px: e.target.value === '' ? undefined : Number(e.target.value) })}
                    data-testid="gallery-phone-height"
                  />
                </div>
                <div>
                  <Label className="text-sm">Max height — mobile (px)</Label>
                  <Input
                    type="number" min="240" max="900"
                    placeholder="(fallback: desktop)"
                    value={gallery.phone_min_height_mobile_px ?? ''}
                    onChange={(e) => updateGallery({ phone_min_height_mobile_px: e.target.value === '' ? undefined : Number(e.target.value) })}
                    data-testid="gallery-phone-height-mobile"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm">Image fit inside screen</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  {[
                    { value: 'cover', label: 'Cover (fill, may crop)' },
                    { value: 'contain', label: 'Contain (fit, may letterbox)' },
                  ].map(opt => {
                    const current = gallery.phone_image_fit || 'cover';
                    const selected = current === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => updateGallery({ phone_image_fit: opt.value })}
                        className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                          selected ? 'border-emerald-500 bg-emerald-100 text-emerald-800' : 'border-gray-200 hover:border-gray-300 text-gray-700 bg-white'
                        }`}
                        data-testid={`gallery-phone-fit-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Status bar + browser toolbar */}
              <div className="bg-white/60 border border-emerald-200/60 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <span className="text-sm font-semibold text-emerald-900 block">Show status bar</span>
                    <p className="text-xs text-emerald-800/70 mt-0.5">Adds the iOS-style time (left) + signal / Wi-Fi / battery icons (right) at the top of the screen.</p>
                  </div>
                  <Switch
                    checked={gallery.phone_show_status_bar !== false}
                    onCheckedChange={(v) => updateGallery({ phone_show_status_bar: v })}
                    data-testid="gallery-phone-statusbar-toggle"
                  />
                </div>
                {gallery.phone_show_status_bar !== false && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-gray-600">Status bar background</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={gallery.phone_status_bar_bg || '#f3f4f6'}
                            onChange={(e) => updateGallery({ phone_status_bar_bg: e.target.value })}
                            className="h-8 w-10 rounded border"
                            data-testid="gallery-phone-statusbar-bg"
                          />
                          <Input
                            type="text"
                            placeholder="auto"
                            value={gallery.phone_status_bar_bg || ''}
                            onChange={(e) => updateGallery({ phone_status_bar_bg: e.target.value || undefined })}
                            className="flex-1 h-8 text-xs font-mono"
                          />
                          {gallery.phone_status_bar_bg && (
                            <button type="button" onClick={() => updateGallery({ phone_status_bar_bg: undefined })} className="text-[11px] text-gray-500 underline">Clear</button>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Status bar icon colour</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={gallery.phone_status_icon_color || '#111827'}
                            onChange={(e) => updateGallery({ phone_status_icon_color: e.target.value })}
                            className="h-8 w-10 rounded border"
                            data-testid="gallery-phone-statusbar-icon-color"
                          />
                          <Input
                            type="text"
                            placeholder="auto"
                            value={gallery.phone_status_icon_color || ''}
                            onChange={(e) => updateGallery({ phone_status_icon_color: e.target.value || undefined })}
                            className="flex-1 h-8 text-xs font-mono"
                          />
                          {gallery.phone_status_icon_color && (
                            <button type="button" onClick={() => updateGallery({ phone_status_icon_color: undefined })} className="text-[11px] text-gray-500 underline">Clear</button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Time display</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <select
                          value={gallery.phone_time_mode || (gallery.phone_status_time ? 'custom' : 'apple')}
                          onChange={(e) => updateGallery({ phone_time_mode: e.target.value })}
                          className="h-8 px-2 text-xs rounded border bg-white"
                          data-testid="gallery-phone-time-mode"
                        >
                          <option value="apple">Apple time (9:41)</option>
                          <option value="system">System time (live)</option>
                          <option value="custom">Custom time</option>
                        </select>
                        {(gallery.phone_time_mode === 'custom' || (!gallery.phone_time_mode && gallery.phone_status_time)) && (
                          <Input
                            type="text"
                            placeholder="e.g. 15:00"
                            maxLength={8}
                            value={gallery.phone_status_time || ''}
                            onChange={(e) => updateGallery({ phone_status_time: e.target.value || undefined })}
                            className="flex-1 h-8 text-xs font-mono"
                            data-testid="gallery-phone-status-time"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between gap-3 pt-2 border-t border-emerald-100">
                  <div>
                    <span className="text-sm font-semibold text-emerald-900 block">Show browser toolbar</span>
                    <p className="text-xs text-emerald-800/70 mt-0.5">Renders a Safari-style URL bar beneath the status bar so the screenshot looks like it's running in the mobile browser.</p>
                  </div>
                  <Switch
                    checked={gallery.phone_show_browser_bar !== false}
                    onCheckedChange={(v) => updateGallery({ phone_show_browser_bar: v })}
                    data-testid="gallery-phone-browser-toggle"
                  />
                </div>
                {gallery.phone_show_browser_bar !== false && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-gray-600">Browser URL shown in toolbar</Label>
                      <Input
                        type="text"
                        placeholder="grassrootscoaching.app"
                        value={gallery.phone_browser_url || ''}
                        onChange={(e) => updateGallery({ phone_browser_url: e.target.value })}
                        className="h-8 text-xs mt-1"
                        data-testid="gallery-phone-browser-url"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Browser bar background</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={gallery.phone_browser_bar_bg || '#f3f4f6'}
                          onChange={(e) => updateGallery({ phone_browser_bar_bg: e.target.value })}
                          className="h-8 w-10 rounded border"
                          data-testid="gallery-phone-browserbar-bg"
                        />
                        <Input
                          type="text"
                          placeholder="auto"
                          value={gallery.phone_browser_bar_bg || ''}
                          onChange={(e) => updateGallery({ phone_browser_bar_bg: e.target.value || undefined })}
                          className="flex-1 h-8 text-xs font-mono"
                        />
                        {gallery.phone_browser_bar_bg && (
                          <button type="button" onClick={() => updateGallery({ phone_browser_bar_bg: undefined })} className="text-[11px] text-gray-500 underline">Clear</button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between gap-3 pt-2 border-t border-emerald-100">
                  <div>
                    <span className="text-sm font-semibold text-emerald-900 block">Drop shadow under phone</span>
                    <p className="text-xs text-emerald-800/70 mt-0.5">Adds a soft floating-card shadow biased toward the bottom of the phone — great on plain backgrounds.</p>
                  </div>
                  <Switch
                    checked={!!gallery.phone_drop_shadow}
                    onCheckedChange={(v) => updateGallery({ phone_drop_shadow: v })}
                    data-testid="gallery-phone-shadow-toggle"
                  />
                </div>

                <div className="flex items-center justify-between gap-3 pt-2 border-t border-emerald-100">
                  <div>
                    <span className="text-sm font-semibold text-emerald-900 block">Floor shadow under phone</span>
                    <p className="text-xs text-emerald-800/70 mt-0.5">Soft elliptical ground shadow PNG, makes the phone feel like it's sitting on a surface. Independent of the drop shadow above.</p>
                  </div>
                  <Switch
                    checked={gallery.phone_floor_shadow === true}
                    onCheckedChange={(v) => updateGallery({ phone_floor_shadow: v })}
                    data-testid="gallery-phone-floor-shadow-toggle"
                  />
                </div>

                {gallery.phone_floor_shadow === true && (
                  <div className="pt-2 pl-4 border-l-2 border-emerald-100" data-testid="gallery-phone-floor-shadow-offset-section">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-sm font-semibold text-emerald-900 block">Shadow vertical position</span>
                        <p className="text-xs text-emerald-800/70 mt-0.5">Nudge the shadow up (under phone) or down (more visible gap). 0 = default.</p>
                      </div>
                      <span className="text-xs font-mono text-emerald-900" data-testid="gallery-phone-floor-shadow-offset-value">
                        {gallery.phone_floor_shadow_offset || 0}px
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-emerald-800/60 font-mono w-10 text-right">-30</span>
                      <input
                        type="range"
                        min="-30"
                        max="30"
                        step="1"
                        value={gallery.phone_floor_shadow_offset || 0}
                        onChange={(e) => updateGallery({ phone_floor_shadow_offset: Number(e.target.value) })}
                        className="flex-1 accent-emerald-600"
                        data-testid="gallery-phone-floor-shadow-offset-slider"
                      />
                      <span className="text-[10px] text-emerald-800/60 font-mono w-10">+30</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Bezel colour</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { value: '#0a0a0a', label: 'Black' },
                    { value: '#9ca3af', label: 'Silver' },
                    { value: '#007a33', label: 'Brand green' },
                    { value: '#1d4ed8', label: 'Blue' },
                    { value: '#f59e0b', label: 'Amber' },
                  ].map(opt => {
                    const selected = (gallery.phone_bezel_color || '').toLowerCase() === opt.value.toLowerCase();
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => updateGallery({ phone_bezel_color: opt.value })}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                          selected ? 'border-emerald-500 bg-white text-emerald-800' : 'border-gray-200 hover:border-gray-300 text-gray-700 bg-white'
                        }`}
                        data-testid={`gallery-phone-bezel-${opt.value.replace('#','')}`}
                      >
                        <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: opt.value }} />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  <input
                    type="color"
                    value={gallery.phone_bezel_color || '#0a0a0a'}
                    onChange={(e) => updateGallery({ phone_bezel_color: e.target.value })}
                    className="h-8 w-12 rounded border border-gray-200 cursor-pointer"
                    data-testid="gallery-phone-bezel-colorpicker"
                    aria-label="Bezel colour picker"
                  />
                  <Input
                    type="text"
                    placeholder="#0a0a0a"
                    value={gallery.phone_bezel_color || ''}
                    onChange={(e) => updateGallery({ phone_bezel_color: e.target.value || undefined })}
                    className="w-32 h-8 text-xs"
                    data-testid="gallery-phone-bezel-hex"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateGallery({
                      phone_width_px: undefined,
                      phone_min_height_px: undefined,
                      phone_min_height_mobile_px: undefined,
                      phone_bezel_color: undefined,
                      phone_image_fit: undefined,
                      phone_status_bar_bg: undefined,
                      phone_status_icon_color: undefined,
                      phone_time_mode: undefined,
                      phone_browser_bar_bg: undefined,
                    })}
                    data-testid="gallery-phone-reset"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reset all
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Images */}
        {images.length === 0 && (
          <div className="text-center py-10 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500">
            No images yet. Click “Add Image” to upload your first slide.
          </div>
        )}

        {images.map((img, index) => (
          <div
            key={img.id || index}
            className="space-y-3"
            data-testid={`gallery-image-row-${index}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">Image {index + 1}</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => moveImage(index, index - 1)}
                  disabled={index === 0}
                  data-testid={`gallery-move-up-${index}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => moveImage(index, index + 1)}
                  disabled={index === images.length - 1}
                  data-testid={`gallery-move-down-${index}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600"
                  onClick={() => removeImage(index)}
                  data-testid={`gallery-remove-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {img.url ? (
              <div className="relative inline-block">
                <img
                  src={resolveImageUrl(img.url)}
                  alt={img.title || `Slide ${index + 1}`}
                  className="h-20 w-auto rounded-md border border-gray-200 object-cover block"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  data-testid={`gallery-image-preview-${index}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => updateImage(index, 'url', '')}
                  className="absolute -top-1.5 -right-1.5 h-6 w-6 p-0 bg-white hover:bg-red-50 text-red-500 rounded-full shadow-sm border border-gray-200"
                  data-testid={`gallery-image-clear-${index}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ) : null}

            <label
              className={`inline-flex items-center gap-2 px-3 py-2.5 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors ${uploadingIndex === index ? 'opacity-50 pointer-events-none' : ''}`}
              data-testid={`gallery-upload-${index}`}
            >
              <Upload className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500">
                {uploadingIndex === index ? 'Uploading...' : img.url ? 'Replace image' : 'Upload image'}
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="hidden"
                onChange={(e) => {
                  handleImageUpload(index, e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>

            <div>
              <Label>Title</Label>
              <Input
                value={img.title || ''}
                onChange={(e) => updateImage(index, 'title', e.target.value)}
                placeholder="Shown below the image"
                data-testid={`gallery-image-title-${index}`}
              />
            </div>

            <div>
              <Label>Subtitle</Label>
              <Input
                value={img.subtitle || ''}
                onChange={(e) => updateImage(index, 'subtitle', e.target.value)}
                placeholder="Optional — shown under the title"
                data-testid={`gallery-image-subtitle-${index}`}
              />
            </div>

            {index < images.length - 1 && <div className="border-t border-gray-100 pt-3" />}
          </div>
        ))}
      </CardContent>
      <style>{`
        @keyframes galleryRowPulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 122, 51, 0.55); }
          70% { box-shadow: 0 0 0 14px rgba(0, 122, 51, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 122, 51, 0); }
        }
        .gallery-row-pulse {
          border-radius: 0.5rem;
          animation: galleryRowPulse 1.1s ease-out 1;
        }
      `}</style>
    </Card>
  );
}

export default GalleryTab;
